<template>
  <figure class="report-chart">
    <figcaption>{{ chart.ticker || chart.title }}</figcaption>
    <p v-if="chart.error" role="status">{{ chart.error }}</p>
    <p v-else-if="isEmpty" role="status">No hay datos disponibles para este período.</p>
    <div v-else ref="canvas" class="report-chart__canvas" :aria-label="chart.ticker || chart.title"></div>
  </figure>
</template>
<script>
import Highcharts from 'highcharts';
import { MODERN_THEME_KEY } from '@/shells/services/modernThemeController';
import { formatReportNumber } from '../models/reportNumbers';
export default {
  name: 'ReportChart',
  inject: { modernTheme: { from: MODERN_THEME_KEY, default: null } },
  props: { chart: { type: Object, required: true } },
  computed: {
    dark() { return this.modernTheme && this.modernTheme.effectiveTheme === 'dark'; },
    isEmpty() {
      if (this.chart.kind === 'stock') return !this.chart.points.length;
      if (this.chart.kind === 'pie') return !Array.isArray(this.chart.data) || !this.chart.data.length;
      return !this.chart.data || !Array.isArray(this.chart.data.series) || !this.chart.data.series.some(series => Array.isArray(series.data) && series.data.some(value => value !== null && value !== '' && Number.isFinite(Number(value))));
    }
  },
  watch: { chart: { handler() { this.$nextTick(this.draw); }, deep: true }, dark() { this.draw(); } },
  mounted() {
    this.draw();
    if (typeof ResizeObserver !== 'undefined') { this.observer = new ResizeObserver(() => { if (this.instance) this.instance.reflow(); }); this.observer.observe(this.$el); }
  },
  beforeDestroy() { if (this.observer) this.observer.disconnect(); if (this.instance) this.instance.destroy(); },
  methods: {
    draw() {
      if (this.instance) { this.instance.destroy(); this.instance = null; }
      if (!this.$refs.canvas || this.chart.error || this.isEmpty) return;
      const text = this.dark ? '#dce6f1' : '#334155';
      const line = this.dark ? '#314256' : '#e2e8f0';
      const stock = this.chart.kind === 'stock';
      const pie = this.chart.kind === 'pie';
      const series = stock ? [{ name: this.chart.ticker, data: this.chart.points, color: '#009fc1' }] : pie ? [{ name: 'Valores', data: this.chart.data }] : this.chart.data.series.map(item => ({ name: String(item.name || ''), data: Array.isArray(item.data) ? item.data.map(value => Number.isFinite(Number(value)) ? Number(value) : null) : [] }));
      this.instance = Highcharts.chart(this.$refs.canvas, {
        chart: { type: stock ? 'line' : this.chart.kind, height: stock ? 330 : 285, backgroundColor: 'transparent', style: { fontFamily: 'inherit' }, zoomType: stock ? 'x' : undefined },
        title: { text: null }, credits: { enabled: false }, exporting: { enabled: false },
        colors: ['#009fc1', '#7b8fde', '#44b88a', '#dfaa54', '#d97883'],
        xAxis: { type: stock ? 'datetime' : 'category', categories: !stock && !pie ? this.chart.data.categories : undefined, lineColor: line, tickColor: line, labels: { style: { color: text } } },
        yAxis: { type: stock ? 'logarithmic' : 'linear', title: { text: null }, gridLineColor: line, labels: { style: { color: text }, formatter() { return formatReportNumber(this.value); } } },
        legend: { enabled: !stock, itemStyle: { color: text }, itemHoverStyle: { color: '#009fc1' } },
        tooltip: { useHTML: false, backgroundColor: this.dark ? '#142235' : '#fff', style: { color: text }, formatter() {
          const label = stock ? new Intl.DateTimeFormat('es-ES').format(new Date(this.x)) : String(this.key || this.series.name || '').replace(/[&<>]/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[char]));
          return `${label}<br/>${formatReportNumber(this.y)}${pie ? ` · ${formatReportNumber(this.percentage)} %` : ''}`;
        } },
        plotOptions: { series: { animation: false, marker: { enabled: false }, borderWidth: 0 }, pie: { dataLabels: { enabled: false }, showInLegend: true } }, series
      });
    }
  }
};
</script>
<style scoped>
.report-chart { margin: 24px 0; min-width: 0; border: 1px solid var(--modern-border); border-radius: 16px; padding: 16px 10px 8px; background: var(--modern-surface); }
.report-chart figcaption { margin: 0 10px 8px; color: var(--modern-text); font-size: 14px; font-weight: 750; }
.report-chart__canvas { width: 100%; min-width: 0; }
.report-chart p { padding: 18px; color: var(--modern-muted); }
</style>
